import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";

export default function Artists() {
  const [artists, setArtists] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/artists"), api.get("/artworks")])
      .then(([ar, aw]) => { setArtists(ar.data); setArtworks(aw.data); })
      .finally(() => setLoading(false));
  }, []);

  const worksByArtist = useMemo(() => {
    const map = {};
    for (const a of artworks) {
      const k = (a.artist || "").toLowerCase();
      if (!map[k]) map[k] = [];
      map[k].push(a);
    }
    return map;
  }, [artworks]);

  // Only show artists who have at least one field (bio, portrait) or a work — keeps it tidy
  const visible = useMemo(() => {
    return artists
      .filter(a => (a.bio || a.portrait_url || a.cv_text || worksByArtist[(a.name || "").toLowerCase()]?.length))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [artists, worksByArtist]);

  return (
    <div data-testid="artists-page">
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-20 md:py-28">
        <div className="overline">Our roster</div>
        <h1 className="display text-6xl md:text-8xl mt-6">Artists.</h1>
        <p className="mt-8 text-base md:text-lg text-muted2 max-w-2xl leading-relaxed">
          A growing community of painters, ceramicists and sculptors whose work has passed through the walls of Carpe Diem. Some show with us regularly, others make one-off appearances. Every piece on our Buy Art page is a slice of their practice.
        </p>

        <div className="mt-16">
          {loading ? (
            <div className="text-muted2">Loading…</div>
          ) : visible.length === 0 ? (
            <div className="text-muted2">No artist profiles yet — check back soon.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-16 gap-x-10">
              {visible.map(a => {
                const slug = a.name.replace(/\s+/g, '-').toLowerCase();
                const works = worksByArtist[a.name.toLowerCase()] || [];
                return (
                  <article key={a.id} className="group" data-testid={`artist-card-${slug}`}>
                    <div className="relative aspect-[4/5] overflow-hidden bg-cream-2 border border-soft mb-5">
                      {/* Main image: prefer an artwork so the practice is visible; fall back to portrait, else initials */}
                      {works[0]?.image_url ? (
                        <div className="w-full h-full flex items-center justify-center p-4">
                          <img
                            src={works[0].image_url}
                            alt={`${a.name} — ${works[0].title || 'artwork'}`}
                            loading="lazy"
                            className="max-w-full max-h-full w-auto h-auto object-contain reveal-img group-hover:scale-[1.02] transition-transform duration-700"
                          />
                        </div>
                      ) : a.portrait_url ? (
                        <img
                          src={a.portrait_url}
                          alt={a.name}
                          loading="lazy"
                          className="w-full h-full object-cover reveal-img group-hover:scale-[1.02] transition-transform duration-700"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center font-serif italic text-5xl text-muted2">
                          {a.name.split(' ').map(w => w[0]).join('')}
                        </div>
                      )}

                      {/* Portrait chip overlay — bottom-left, ring for contrast. Only if we have both a work AND a portrait. */}
                      {a.portrait_url && works[0]?.image_url && (
                        <div className="absolute left-4 bottom-4 flex items-center gap-3 bg-white/85 backdrop-blur-md pr-4 pl-1 py-1 border border-soft rounded-full shadow-sm">
                          <img
                            src={a.portrait_url}
                            alt={a.name}
                            loading="lazy"
                            className="w-10 h-10 rounded-full object-cover ring-2 ring-white"
                            data-testid={`artist-portrait-chip-${slug}`}
                          />
                          <span className="text-[11px] tracking-wide font-medium text-charcoal">{a.name}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-baseline justify-between gap-3">
                      <h2 className="font-serif text-3xl leading-tight">{a.name}</h2>
                      <div className="text-xs text-muted2 shrink-0">{works.length} work{works.length !== 1 ? "s" : ""}</div>
                    </div>

                    {a.bio && <p className="mt-3 text-sm leading-relaxed text-muted2 line-clamp-4">{a.bio}</p>}

                    <div className="mt-5 flex flex-wrap gap-4 text-xs">
                      {works.length > 0 && (
                        <Link
                          to="/gallery/buy"
                          state={{ open: a.name }}
                          className="link-hover inline-flex items-center gap-1"
                          data-testid={`artist-card-view-works-${slug}`}
                        >
                          View works <ArrowUpRight size={12} />
                        </Link>
                      )}
                      {a.website && <a href={a.website} target="_blank" rel="noreferrer" className="link-hover inline-flex items-center gap-1">Website <ArrowUpRight size={12}/></a>}
                      {a.instagram && (
                        <a
                          href={a.instagram.startsWith("http") ? a.instagram : `https://instagram.com/${a.instagram.replace("@","")}`}
                          target="_blank"
                          rel="noreferrer"
                          className="link-hover inline-flex items-center gap-1"
                        >Instagram <ArrowUpRight size={12}/></a>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>

        <div className="hairline my-20" />

        <div className="grid md:grid-cols-2 gap-8 items-baseline">
          <div>
            <div className="overline">Exhibit with us</div>
            <h3 className="display text-3xl md:text-4xl mt-4">Are you an artist?</h3>
          </div>
          <div>
            <p className="text-base text-muted2 leading-relaxed">
              We accept exhibition applications on a rolling basis. If you have a body of work you would like to show in our gallery in Majorda, we would love to see it.
            </p>
            <Link to="/gallery/apply" className="btn-primary mt-6" data-testid="artists-apply-cta">
              Apply to exhibit
              <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
